import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import {
  FileText, Download, Eye, Search, Zap,
  BookOpen, Calendar, ClipboardList, LayoutGrid, Clock,
  CheckCircle, AlertCircle, ChevronDown, ChevronRight, SlidersHorizontal, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: 'RPP',        label: 'Modul Ajar', icon: FileText,      color: '#3B82F6', bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE', stripe: 'from-blue-500 to-blue-600',   desc: 'Modul Pembelajaran' },
  { key: 'Modul Ajar', label: 'Modul Ajar', icon: BookOpen,      color: '#A855F7', bg: '#FAF5FF', text: '#7E22CE', border: '#E9D5FF', stripe: 'from-purple-500 to-purple-600', desc: 'Modul Pembelajaran Lengkap' },
  { key: 'KKTP',       label: 'KKTP',       icon: ClipboardList, color: '#10B981', bg: '#F0FDF4', text: '#065F46', border: '#A7F3D0', stripe: 'from-emerald-500 to-emerald-600', desc: 'Kriteria Ketercapaian Tujuan Pembelajaran' },
  { key: 'Promes',     label: 'Promes',     icon: Calendar,      color: '#F59E0B', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', stripe: 'from-amber-500 to-amber-600',   desc: 'Program Semester' },
  { key: 'Prota',      label: 'Prota',      icon: LayoutGrid,    color: '#EC4899', bg: '#FFF1F2', text: '#9F1239', border: '#FBCFE8', stripe: 'from-pink-500 to-pink-600',    desc: 'Program Tahunan' },
  { key: 'Jadwal',     label: 'Jadwal',     icon: Clock,         color: '#06B6D4', bg: '#ECFEFF', text: '#164E63', border: '#A5F3FC', stripe: 'from-cyan-500 to-cyan-600',    desc: 'Jadwal Mengajar' },
];

const TYPE_MAP = Object.fromEntries(DOC_TYPES.map(t => [t.key, t]));

const formatDate = d => d
  ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
  : '-';

const formatDateGroup = d => {
  if (!d) return 'Lainnya';
  const now = new Date();
  const date = new Date(d);
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hari Ini';
  if (diffDays <= 7) return '7 Hari Terakhir';
  if (diffDays <= 30) return '30 Hari Terakhir';
  return date.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
};

// ── Doc Card ────────────────────────────────────────────────────────────────
const DocCard = ({ doc, onView, onDownload }) => {
  const t = TYPE_MAP[doc.document_type] || TYPE_MAP['RPP'];
  const Icon = t.icon;
  const isFailed = doc.content?.structured?.parse_error;

  return (
    <div className="bg-white border rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group flex flex-col"
      style={{ borderColor: t.border }}>

      {/* Top gradient stripe */}
      <div className={`h-1 bg-gradient-to-r ${t.stripe}`} />

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: t.bg }}>
              <Icon size={15} style={{ color: t.color }} />
            </div>
            <div>
              <span className="text-xs font-bold" style={{ color: t.text }}>{t.label}</span>
              <p className="text-slate-400 text-[10px] leading-tight">{t.desc}</p>
            </div>
          </div>
          {isFailed ? (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle size={10} className="text-red-400" />
              <span className="text-[10px] font-semibold text-red-500">Gagal</span>
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-green-50 border border-green-100">
              <CheckCircle size={10} className="text-green-500" />
              <span className="text-[10px] font-semibold text-green-600">Selesai</span>
            </span>
          )}
        </div>

        {/* Subject name */}
        <div className="flex-1">
          <h3 className="font-bold text-slate-800 text-sm leading-snug mb-1.5"
            style={{ fontFamily: "'Outfit', sans-serif" }}>
            {doc.subject_name}
          </h3>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
              Kelas {doc.kelas} {doc.jenjang}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
              Sem. {doc.semester}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
              {doc.tahun_ajaran}
            </span>
          </div>
        </div>

        {/* Date */}
        <p className="text-[11px] text-slate-400">{formatDate(doc.created_at)}</p>

        {/* Actions */}
        <div className="flex gap-2 pt-2.5 border-t border-slate-100">
          <button
            onClick={() => onView(doc._id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-slate-200
                       text-slate-600 text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors">
            <Eye size={12} />
            Lihat
          </button>
          {isFailed ? (
            <button
              onClick={() => onView(doc._id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold
                         bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors">
              <AlertCircle size={12} />
              Refund
            </button>
          ) : (
            <a
              href={`${API}/documents/${doc._id}/download`}
              onClick={e => onDownload && onDownload(e)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-white text-xs font-semibold
                         hover:opacity-90 transition-opacity"
              style={{ background: `linear-gradient(135deg, ${t.color}cc, ${t.color})` }}>
              <Download size={12} />
              Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
export const MyDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('semua');
  const [groupBy, setGroupBy] = useState('tanggal'); // 'tanggal' | 'mapel'
  const [expandedCategories, setExpandedCategories] = useState(
    Object.fromEntries(DOC_TYPES.map(t => [t.key, true]))
  );
  const navigate = useNavigate();

  const toggleCategory = key =>
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));

  const allExpanded = Object.values(expandedCategories).every(Boolean);
  const toggleAll = () => {
    const next = !allExpanded;
    setExpandedCategories(Object.fromEntries(DOC_TYPES.map(t => [t.key, next])));
  };

  useEffect(() => {
    axios.get(`${API}/documents`, { withCredentials: true })
      .then(r => setDocuments(r.data))
      .catch(err => console.error('Gagal memuat dokumen:', err))
      .finally(() => setLoading(false));
  }, []);

  // Count per type
  const countByType = DOC_TYPES.reduce((acc, t) => {
    acc[t.key] = documents.filter(d => d.document_type === t.key).length;
    return acc;
  }, {});

  // Filtered docs
  const filtered = documents.filter(doc => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      doc.document_type?.toLowerCase().includes(q) ||
      doc.subject_name?.toLowerCase().includes(q) ||
      doc.name?.toLowerCase().includes(q) ||
      String(doc.kelas).includes(q) ||
      doc.jenjang?.toLowerCase().includes(q);
    const matchTab = activeTab === 'semua' || doc.document_type === activeTab;
    return matchSearch && matchTab;
  });

  // Group filtered docs
  const grouped = (() => {
    if (groupBy === 'mapel') {
      const map = {};
      filtered.forEach(doc => {
        const key = doc.subject_name || 'Lainnya';
        if (!map[key]) map[key] = [];
        map[key].push(doc);
      });
      return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }
    // group by date
    const map = {};
    filtered.forEach(doc => {
      const key = formatDateGroup(doc.created_at);
      if (!map[key]) map[key] = [];
      map[key].push(doc);
    });
    const order = ['Hari Ini', '7 Hari Terakhir', '30 Hari Terakhir'];
    const sorted = Object.entries(map).sort(([a], [b]) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return b.localeCompare(a);
    });
    return sorted;
  })();

  return (
    <DashboardLayout>
      <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Dokumen Saya
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {documents.length} dokumen tersimpan
            </p>
          </div>
          <button
            onClick={() => navigate('/documents/generate')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
            <Zap size={15} />
            Buat Dokumen Baru
          </button>
        </div>

        {/* ── Stats bar ── */}
        {!loading && documents.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
            {DOC_TYPES.map(t => {
              const count = countByType[t.key] || 0;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(activeTab === t.key ? 'semua' : t.key)}
                  className={`rounded-2xl p-3 text-center transition-all border ${
                    activeTab === t.key
                      ? 'shadow-md -translate-y-0.5'
                      : 'bg-white hover:shadow-sm hover:-translate-y-0.5'
                  }`}
                  style={activeTab === t.key
                    ? { background: t.bg, borderColor: t.border }
                    : { borderColor: '#F1F5F9' }
                  }>
                  <Icon size={16} className="mx-auto mb-1" style={{ color: activeTab === t.key ? t.color : '#94A3B8' }} />
                  <div className="text-lg font-bold leading-none mb-0.5"
                    style={{ color: activeTab === t.key ? t.color : '#1E293B' }}>
                    {count}
                  </div>
                  <div className="text-[10px] font-semibold" style={{ color: activeTab === t.key ? t.text : '#94A3B8' }}>
                    {t.label}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Search & controls ── */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-3.5 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Cari mata pelajaran, jenjang, kelas..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400
                           focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Active tab chip */}
            {activeTab !== 'semua' && (() => {
              const t = TYPE_MAP[activeTab];
              return (
                <button
                  onClick={() => setActiveTab('semua')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors"
                  style={{ background: t.bg, borderColor: t.border, color: t.text }}>
                  Hanya {t.label} <X size={11} />
                </button>
              );
            })()}

            {/* Group by */}
            <div className="flex items-center gap-1.5 bg-slate-100 rounded-xl p-1">
              <SlidersHorizontal size={13} className="text-slate-400 ml-1.5" />
              <button
                onClick={() => setGroupBy('tanggal')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  groupBy === 'tanggal' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                Tanggal
              </button>
              <button
                onClick={() => setGroupBy('mapel')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  groupBy === 'mapel' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                Mapel
              </button>
            </div>
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin" />
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-14 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' }}>
              <FileText size={28} className="text-blue-400" />
            </div>
            <h3 className="font-bold text-slate-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {search || activeTab !== 'semua' ? 'Tidak ada dokumen yang cocok' : 'Belum ada dokumen'}
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              {search || activeTab !== 'semua'
                ? 'Coba ubah filter atau kata kunci pencarian'
                : 'Mulai buat dokumen pembelajaran pertama Anda dengan bantuan AI'}
            </p>
            {!search && activeTab === 'semua' && (
              <button
                onClick={() => navigate('/documents/generate')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                <Zap size={15} />
                Buat Dokumen Pertama
              </button>
            )}
          </div>
        )}

        {/* ── Category sections when tab = semua & no search ── */}
        {!loading && filtered.length > 0 && activeTab === 'semua' && !search && (
          <div className="space-y-3">

            {/* Expand / Collapse all */}
            <div className="flex justify-end mb-1">
              <button
                onClick={toggleAll}
                className="text-xs font-semibold text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1">
                {allExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                {allExpanded ? 'Tutup Semua' : 'Buka Semua'}
              </button>
            </div>

            {DOC_TYPES.filter(t => countByType[t.key] > 0).map(t => {
              const Icon = t.icon;
              const docs = documents.filter(d => d.document_type === t.key);
              const isOpen = expandedCategories[t.key];

              return (
                <div key={t.key}
                  className="bg-white border rounded-2xl overflow-hidden transition-all"
                  style={{ borderColor: isOpen ? t.border : '#F1F5F9' }}>

                  {/* ── Accordion header ── */}
                  <button
                    onClick={() => toggleCategory(t.key)}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      {/* Icon */}
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: t.bg }}>
                        <Icon size={17} style={{ color: t.color }} />
                      </div>
                      {/* Label */}
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t.label}
                          </span>
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: t.bg, color: t.text }}>
                            {docs.length} dokumen
                          </span>
                        </div>
                        <p className="text-slate-400 text-[11px] leading-tight">{t.desc}</p>
                      </div>
                    </div>

                    {/* Right side: progress bar + chevron */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Mini progress dots */}
                      <div className="hidden sm:flex items-center gap-1">
                        {docs.slice(0, 5).map((_, i) => (
                          <div key={i} className="w-1.5 h-1.5 rounded-full"
                            style={{ background: t.color, opacity: 0.6 + i * 0.08 }} />
                        ))}
                        {docs.length > 5 && (
                          <span className="text-[10px] text-slate-400 ml-0.5">+{docs.length - 5}</span>
                        )}
                      </div>
                      <ChevronDown
                        size={16}
                        className="text-slate-400 transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                      />
                    </div>
                  </button>

                  {/* ── Accordion body ── */}
                  {isOpen && (
                    <div className="px-4 pb-4 border-t" style={{ borderColor: t.border }}>
                      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
                        {docs.map(doc => (
                          <DocCard
                            key={doc._id}
                            doc={doc}
                            onView={id => navigate(`/documents/${id}`)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Filtered view (tab selected or search active) ── */}
        {!loading && filtered.length > 0 && (activeTab !== 'semua' || search) && (
          <div className="space-y-6">
            {grouped.map(([groupLabel, docs]) => (
              <div key={groupLabel}>
                {/* Group label */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{groupLabel}</span>
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-xs text-slate-400">{docs.length} dokumen</span>
                </div>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {docs.map(doc => (
                    <DocCard
                      key={doc._id}
                      doc={doc}
                      onView={id => navigate(`/documents/${id}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};
